import React, { useEffect, useRef } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { REFUND_REASONS, RefundTenderWire } from '../../api';
import { PriceBasis, RefundSelection, formatCents, refundTotals } from '../money';
import { COLORS } from '../theme';

type Props = {
  open: boolean;
  selections: RefundSelection[];
  /** Basis of the original sale — an owner withdrawal returns no money,
   *  so the figures shown are zero. */
  basis: PriceBasis;
  reason: string;
  tender: RefundTenderWire;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/** Confirmation for a refund. Modelled on the employee-discount dialog
 *  but deliberately quieter — refunds are routine, and a screaming
 *  full-screen warning on a routine action trains people to dismiss
 *  warnings. Cancel is still the default action. */
const RefundConfirmDialog: React.FC<Props> = ({
  open,
  selections,
  basis,
  reason,
  tender,
  busy,
  onCancel,
  onConfirm,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const totals = refundTotals(selections);
  const reversal = basis === 'owner';
  const reasonLabel =
    REFUND_REASONS.find((r) => r.value === reason)?.label ?? reason;
  const units = selections.reduce((n, s) => n + s.quantity, 0);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={(_e, r) => {
        if (r !== 'backdropClick' && !busy) onCancel();
      }}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <UndoIcon sx={{ color: COLORS.danger, fontSize: 32 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem' }}>
            {reversal ? 'Confirm return to stock' : 'Confirm refund'}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: COLORS.dangerBg,
            border: `2px solid ${COLORS.danger}`,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <Row
            label={reversal ? 'Reversing' : 'Refunding'}
            value={formatCents(totals.total)}
            emphasise
          />
          {reversal && (
            // The figure above is what comes off the books, not out of
            // the till — worth saying before someone counts cash.
            <Typography variant="body2" sx={{ color: COLORS.danger, mb: 0.5 }}>
              Owner withdrawal: no cash leaves the till. The cost and HST
              recorded when it was taken come back off.
            </Typography>
          )}
          <Row
            label="Items"
            value={`${Number.isInteger(units) ? units : units.toFixed(2)} across ${
              selections.length
            } ${selections.length === 1 ? 'line' : 'lines'}`}
          />
          <Row label="Reason" value={reasonLabel} />
          <Row
            label="Refund to"
            value={tender === 'cash' ? 'Cash — from the till' : 'Card'}
          />
        </Box>

        {/* The honesty clause. R2 records; it does not move money. */}
        <Typography sx={{ color: COLORS.textMuted, mb: 3 }}>
          {tender === 'cash'
            ? 'This records the refund. Hand the cash from the till.'
            : 'This records the refund only — run the card refund on the terminal separately.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            ref={cancelRef}
            onClick={onCancel}
            disabled={busy}
            variant="contained"
            sx={{ flex: 1, py: 1.75, fontWeight: 700 }}
          >
            CANCEL
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            variant="outlined"
            color="error"
            sx={{ flex: 1, py: 1.75, fontWeight: 700 }}
          >
            {busy ? 'RECORDING…' : 'CONFIRM REFUND'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: string; emphasise?: boolean }> = ({
  label,
  value,
  emphasise,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
    <Typography sx={{ color: COLORS.textMuted }}>{label}</Typography>
    <Typography
      sx={{
        fontWeight: emphasise ? 800 : 600,
        fontSize: emphasise ? '1.6rem' : '1rem',
        color: emphasise ? COLORS.danger : COLORS.text,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default RefundConfirmDialog;
